import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { STATUS_LABELS } from "@/components/admin/OrdersTable";

const notifyError = (err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);

export const useAdminData = (user) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [o, s, r, m] = await Promise.all([
        api.get("/admin/orders"), api.get("/admin/stats"), api.get("/reviews"), api.get("/admin/messages"),
      ]);
      setOrders(o.data);
      setStats(s.data);
      setReviews(r.data.reviews);
      setMessages(m.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin");
      else if (err.response?.status === 403) navigate("/cuenta/panel");
      else notifyError(err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (user === false) navigate("/admin");
    else if (user && user.role !== "admin") navigate("/cuenta/panel");
    else if (user) load();
  }, [user, navigate, load]);

  const changeStatus = async (orderId, status) => {
    try {
      const { data } = await api.patch(`/admin/orders/${orderId}/status`, { status });
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status } : o)));
      toast.success(
        data.email_sent
          ? `Estado "${STATUS_LABELS[status]}" — aviso enviado al cliente por email`
          : `Estado actualizado a "${STATUS_LABELS[status]}"`
      );
    } catch (err) {
      notifyError(err);
    }
  };

  const deleteMessage = async (id) => {
    try {
      await api.delete(`/admin/messages/${id}`);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success("Mensaje eliminado");
    } catch (err) {
      notifyError(err);
    }
  };

  const deleteReview = async (id) => {
    try {
      await api.delete(`/admin/reviews/${id}`);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      toast.success("Reseña eliminada");
    } catch (err) {
      notifyError(err);
    }
  };

  return { orders, stats, reviews, messages, loading, changeStatus, deleteMessage, deleteReview };
};
